import { Department, DepartmentFormData, DepartmentListResponse, DepartmentFilterParams } from '../types/department.types';
import { departmentApi } from '../api/departmentApi';

/**
 * Service for handling department-related business logic
 */
export const DepartmentService = {
  /**
   * Get all departments with pagination and filters
   */
  async getDepartments(params: DepartmentFilterParams): Promise<DepartmentListResponse> {
    return departmentApi.getAll(params);
  },

  /**
   * Get a single department by ID
   */
  async getDepartmentById(id: string): Promise<Department> {
    return departmentApi.getById(id);
  },

  /**
   * Create a new department
   */
  async createDepartment(data: DepartmentFormData): Promise<Department> {
    return departmentApi.create(data);
  },

  /**
   * Update an existing department
   */
  async updateDepartment(id: string, data: Partial<DepartmentFormData>): Promise<Department> {
    return departmentApi.update(id, data);
  },

  /**
   * Delete a department
   */
  async deleteDepartment(id: string): Promise<void> {
    return departmentApi.delete(id);
  },

  /**
   * Toggle department status (active/inactive)
   */
  async toggleDepartmentStatus(id: string, isActive: boolean): Promise<Department> {
    return departmentApi.updateStatus(id, { isActive });
  },

  /**
   * Search departments by name or code
   */
  async searchDepartments(query: string): Promise<Department[]> {
    const response = await departmentApi.getAll({ search: query, limit: 10 });
    return response.data;
  },

  /**
   * Check if department code already exists
   */
  async checkCodeExists(code: string, excludeId?: string): Promise<boolean> {
    try {
      const response = await departmentApi.getAll({ code, limit: 1 });
      if (excludeId) {
        return response.data.some(dept => dept.code === code && dept.id !== excludeId);
      }
      return response.data.length > 0;
    } catch (error) {
      console.error('Error checking department code:', error);
      return false;
    }
  },
};

export default DepartmentService;
